import React from 'react';
import PropTypes from 'prop-types';
import MuiAlert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { statuses, SUPPORT_CANCELLATION_NAME } from 'constants/booking';
import { getUserDisplayName } from 'logic/userName';
import moment from 'logic/moment';

const MESSAGES = {
  [statuses.PENDING]: (booking) => ({
    severity: 'info',
    title: <FormattedMessage id="pending" defaultMessage="Pending" />,
    content: (
      <FormattedMessage
        id="bookinginfo.status.expiration"
        defaultMessage="Expires on {date}"
        values={{
          date: moment(booking.booking_expiration_date).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.CANCELLED]: (booking, intl) => {
    // Mirrors timeline() in api logic/booking.js -- keep the two in step. The
    // reason marker has to win over the id, or a support user who is also this
    // booking's host gets named personally for a support action.
    const cancellation = booking.Cancellation;
    const registeredBySupport =
      !cancellation || cancellation.cancellation_reason === SUPPORT_CANCELLATION_NAME;
    let cancelledBy = SUPPORT_CANCELLATION_NAME;

    if (!registeredBySupport) {
      if (cancellation.user_id === booking.Guest.id) {
        cancelledBy = getUserDisplayName(booking.Guest, intl);
      } else if (cancellation.user_id === booking.Host.id) {
        cancelledBy = getUserDisplayName(booking.Host, intl);
      }
    }

    // The API derives CANCELLED from cancelled_at, so a missing date should not
    // reach us -- but moment(undefined) is *now* and reports itself as valid, so
    // the raw value is checked too: a fabricated cancellation date shown to a
    // host is worse than saying nothing about when.
    const cancelledAt = booking.cancelled_at ? moment(booking.cancelled_at) : null;

    return {
      severity: 'error',
      title: <FormattedMessage id="cancelled" defaultMessage="Cancelled" />,
      content:
        cancelledAt && cancelledAt.isValid() ? (
          <FormattedMessage
            id="bookinginfo.status.cancelled"
            defaultMessage="Cancelled on {date} by {user}"
            values={{ date: cancelledAt.format('LLL'), user: cancelledBy }}
          />
        ) : (
          <FormattedMessage
            id="bookinginfo.status.cancelledNoDate"
            defaultMessage="Cancelled by {user}"
            values={{ user: cancelledBy }}
          />
        ),
    };
  },
  [statuses.PAID]: (booking) => ({
    severity: 'success',
    title: <FormattedMessage id="paid" defaultMessage="Paid" />,
    content: (
      <FormattedMessage
        id="bookinginfo.status.paid"
        defaultMessage="Paid on {date}"
        values={{
          date: moment(booking.paid_at).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.EXPIRED]: (booking) => ({
    severity: 'error',
    title: <FormattedMessage id="expired" defaultMessage="Expired" />,
    content: (
      <FormattedMessage
        id="bookinginfo.status.expired"
        defaultMessage="Expired on {date}"
        values={{
          date: moment(booking.booking_expiration_date).format('LLL'),
        }}
      />
    ),
  }),
  [statuses.FINISHED]: (booking) => ({
    severity: 'success',
    title: <FormattedMessage id="finished" defaultMessage="Finished" />,
    content: (
      <FormattedMessage
        id="bookinginfo.status.finished"
        defaultMessage="Finished on {date}"
        values={{
          date: moment(booking.finished_at).format('LLL'),
        }}
      />
    ),
  }),
};

const Content = ({ booking }) => {
  const intl = useIntl();

  if (!MESSAGES[booking.status]) {
    return null;
  }

  const { severity, title, content } = MESSAGES[booking.status](booking, intl);

  return (
    <MuiAlert severity={severity} variant="filled">
      <Typography style={{ textTransform: 'uppercase' }}>{title}</Typography>
      {content}
    </MuiAlert>
  );
};

Content.propTypes = {
  booking: PropTypes.shape({
    status: PropTypes.string,
  }),
};

const PrintStatus = ({ booking }) => {
  const intl = useIntl();

  if (!MESSAGES[booking.status]) {
    return null;
  }

  const { content } = MESSAGES[booking.status](booking, intl);

  return <Box>{content}</Box>;
};

PrintStatus.propTypes = {
  booking: PropTypes.shape({
    status: PropTypes.string,
  }),
};

const Status = ({ booking }) => {
  const printing = useMediaQuery('@media print');

  if (printing) {
    return <PrintStatus booking={booking} />;
  }

  return (
    <Box mt={1} textAlign="left">
      <Content booking={booking} />
    </Box>
  );
};

Status.propTypes = {
  booking: PropTypes.shape({}),
};

export default Status;
